import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Factory, Package } from "lucide-react";
import SearchPicker from "@/components/SearchPicker";
import { toast } from "sonner";
import { formatVariantLabel, sortVariants } from "@/lib/variants";

/**
 * MakeToStockDialog — FAZ 1 Stok İçin Üretim.
 *
 * Reuse:
 *   - `SearchPicker` (server-side arama, `/api/products?search=`)
 *   - `POST /api/orders/make-to-stock` (backend endpoint zaten hazır)
 *   - Varyant görselleri için `formatVariantLabel` / `sortVariants`
 *
 * Prop:
 *   open, onOpenChange   — dialog kontrolü
 *   initialProduct       — Ürünler sayfasında seçili/aktif ürün varsa pre-fill
 */
export default function MakeToStockDialog({ open, onOpenChange, initialProduct = null }) {
  const navigate = useNavigate();
  const [product, setProduct] = useState(initialProduct || null);
  const [variants, setVariants] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [warehouseId, setWarehouseId] = useState("");
  const [qtyByVariant, setQtyByVariant] = useState({}); // {variant_id: qty}
  const [priority, setPriority] = useState("normal");
  const [notes, setNotes] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Ürün ilk açılışta/prop değişiminde
  useEffect(() => {
    if (open) {
      setProduct(initialProduct || null);
      setQtyByVariant({});
      setPriority("normal");
      setNotes("");
      setDueDate("");
    }
  }, [open, initialProduct]);

  // Varyantları + depoları çek
  useEffect(() => {
    if (!open) return;
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/warehouses");
        if (alive) setWarehouses(Array.isArray(data) ? data : []);
      } catch { /* ignore */ }
    })();
    return () => { alive = false; };
  }, [open]);

  useEffect(() => {
    if (!product?.id) { setVariants([]); return; }
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.get(`/products/${product.id}/variants`);
        const arr = Array.isArray(data) ? data : [];
        setVariants(sortVariants(arr));
      } catch (e) {
        toast.error(formatApiError(e, "Varyantlar yüklenemedi"));
      } finally {
        setLoading(false);
      }
    })();
  }, [product?.id]);

  const totalQty = useMemo(
    () => Object.values(qtyByVariant).reduce((s, v) => s + (Number(v) || 0), 0),
    [qtyByVariant],
  );

  const setQty = (vid, val) => {
    const n = Math.max(0, parseInt(val || 0, 10) || 0);
    setQtyByVariant((prev) => ({ ...prev, [vid]: n }));
  };

  const canSave = product?.id && warehouseId && totalQty > 0 && !saving;

  const handleSubmit = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const lines = Object.entries(qtyByVariant)
        .filter(([, q]) => Number(q) > 0)
        .map(([variant_id, quantity]) => ({ variant_id, quantity: Number(quantity) }));

      // Varyantsız ürün desteği: en az bir varyant olmayabilir → line_id="" ile toplam ver
      const payload = {
        product_id: product.id,
        warehouse_id: warehouseId,
        lines: lines.length > 0 ? lines : [{ variant_id: "", quantity: totalQty }],
        priority,
        notes,
        due_date: dueDate || null,
      };

      const { data } = await api.post("/orders/make-to-stock", payload);
      toast.success(`Stok üretim emri oluşturuldu: ${data.order_no || data.id}`);
      onOpenChange(false);
      // Kullanıcı mevcut Üretim & Operasyon ekranında takip etsin
      navigate("/uretim");
    } catch (e) {
      toast.error(formatApiError(e, "Kaydedilemedi"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-900 border-slate-800 max-w-3xl" data-testid="mts-dialog">
        <DialogHeader>
          <DialogTitle className="uppercase tracking-widest text-xs text-slate-400 font-mono flex items-center gap-2">
            <Factory className="w-4 h-4 text-cyan-400" />
            Stok İçin Üretim
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          {/* Ürün seçici */}
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Ürün</Label>
            <div className="mt-1">
              <SearchPicker
                items={product ? [product] : []}
                value={product?.id || ""}
                onChange={async (pid) => {
                  if (!pid) { setProduct(null); return; }
                  try {
                    const { data } = await api.get(`/products/${pid}`);
                    setProduct(data);
                  } catch { /* ignore */ }
                }}
                selectedItem={product || undefined}
                getLabel={(p) => p.name}
                getSubtitle={(p) => `${p.category || "—"} · ${p.variant_count || 0} varyant`}
                placeholder="Ürün ara — en az 2 harf…"
                testId="mts-product"
                onSearch={async (q) => {
                  try {
                    const { data } = await api.get("/products", { params: { search: q, limit: 200 } });
                    return Array.isArray(data) ? data : [];
                  } catch { return []; }
                }}
              />
            </div>
          </div>

          {/* Varyant grid */}
          {product && (
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">
                Varyant × Adet
                <span className="ml-2 text-cyan-400">Toplam: {totalQty}</span>
              </Label>
              <div className="mt-1 rounded-sm border border-slate-800 bg-slate-950/50 max-h-64 overflow-auto">
                {loading ? (
                  <div className="p-4 text-center text-xs text-slate-500 flex items-center justify-center gap-2">
                    <Loader2 className="w-3 h-3 animate-spin" /> Varyantlar yükleniyor…
                  </div>
                ) : variants.length === 0 ? (
                  <div className="p-4 text-center text-xs text-slate-500 italic">
                    Bu ürünün varyantı yok — üretim varyantsız kaydedilecek.
                    <div className="mt-3">
                      <Input
                        type="number" min={0}
                        placeholder="Toplam üretim adedi"
                        value={qtyByVariant["__none__"] || ""}
                        onChange={(e) => setQty("__none__", e.target.value)}
                        className="bg-slate-950 border-slate-700 rounded-sm h-8 text-xs w-40 font-mono mx-auto"
                        data-testid="mts-qty-novariant"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="divide-y divide-slate-800">
                    {variants.map((v) => (
                      <div key={v.id} className="flex items-center justify-between px-3 py-2">
                        <div className="flex items-center gap-2 text-xs">
                          <Package className="w-3 h-3 text-slate-500" />
                          <span className="text-slate-300">{formatVariantLabel(v.attributes)}</span>
                          <span className="text-[10px] text-slate-600 font-mono">{v.sku}</span>
                        </div>
                        <Input
                          type="number" min={0}
                          value={qtyByVariant[v.id] || ""}
                          onChange={(e) => setQty(v.id, e.target.value)}
                          className="bg-slate-950 border-slate-700 rounded-sm h-7 text-xs w-24 font-mono text-right"
                          data-testid={`mts-qty-${v.id}`}
                          placeholder="0"
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Depo */}
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Hedef Depo</Label>
            <div className="mt-1">
              <Select value={warehouseId} onValueChange={setWarehouseId}>
                <SelectTrigger className="bg-slate-950 border-slate-700 rounded-sm h-9 text-xs" data-testid="mts-warehouse">
                  <SelectValue placeholder="Depo seç" />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-800">
                  {warehouses.map((w) => (
                    <SelectItem key={w.id} value={w.id} className="text-xs" data-testid={`mts-warehouse-${w.id}`}>
                      {w.name} <span className="text-slate-500 ml-2">{w.code}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Öncelik + Tarih + Not */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Öncelik</Label>
              <Select value={priority} onValueChange={setPriority}>
                <SelectTrigger className="bg-slate-950 border-slate-700 rounded-sm h-9 text-xs mt-1" data-testid="mts-priority">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-800">
                  <SelectItem value="dusuk" className="text-xs">Düşük</SelectItem>
                  <SelectItem value="normal" className="text-xs">Normal</SelectItem>
                  <SelectItem value="yuksek" className="text-xs">Yüksek</SelectItem>
                  <SelectItem value="acil" className="text-xs">Acil</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Termin (opsiyonel)</Label>
              <Input
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                className="bg-slate-950 border-slate-700 rounded-sm h-9 text-xs mt-1"
                data-testid="mts-due-date"
              />
            </div>
          </div>

          <div>
            <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Not</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="bg-slate-950 border-slate-700 rounded-sm text-xs mt-1"
              placeholder="Üretim notları (opsiyonel)"
              data-testid="mts-notes"
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="bg-transparent border-slate-700 hover:bg-slate-800 rounded-sm uppercase tracking-wider text-xs"
            data-testid="mts-cancel-btn"
          >
            İptal
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!canSave}
            className="bg-cyan-600 hover:bg-cyan-500 rounded-sm uppercase tracking-wider text-xs font-semibold disabled:opacity-50"
            data-testid="mts-save-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Factory className="w-4 h-4 mr-2" />}
            Üretim Emri Oluştur ({totalQty})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
